import {setInShallowClone} from '../../../util/utility';
import * as PanelTypes from '../panel/types';
import * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {ActionType, ViewReducerState} from '../reducerSupport';
import * as Actions from './actions';
import {arrayInsert, arrayRemove} from './arrayMove';
import * as Types from './types';

/**
 * This actions handles moving the active panel to the target section's position in the array.
 * This reorders the panelRefs in the panelBankSectionConfig. This assumes you're moving the
 * panel TO A DIFFERENT SECTION. Please use movePanel2 for moving a panel WITHIN THE SAME SECTION.
 *
 * @param ref This is the panel bank config ref
 * @param active This is the panel that is being moved
 * @param target This is the info that indicates what position the active panel will be moved to and which section
 */
export const movePanelToDiffSection = (
  state: ViewReducerState,
  ref: Types.Ref,
  active: {
    panelRef: PanelTypes.Ref;
    sectionRef: PanelBankSectionConfigTypes.Ref;
  },
  target: {
    panelRef: PanelTypes.Ref | null; // a section can be empty, so the panelRef can be null
    sectionRef: PanelBankSectionConfigTypes.Ref;
    insertDir: 'before' | 'after'; // This is the direction the panel will be inserted in the target section
  }
): [ViewReducerState, ActionType] => {
  const activeSection =
    state.parts[active.sectionRef.type][active.sectionRef.id];
  const activePanelIndex = activeSection.panelRefs.findIndex(
    panelRef => panelRef.id === active.panelRef.id
  );

  // If the target panelRef is null, we know that the target section is empty
  // and we'll insert the panel at the first position
  const targetSection =
    state.parts[target.sectionRef.type][target.sectionRef.id];
  const targetPanelIndex =
    target.panelRef == null
      ? 0
      : targetSection.panelRefs.findIndex(
          panelRef => panelRef.id === target.panelRef?.id
        );

  // Validate that active panel and target section exist
  if (activePanelIndex === -1 || targetPanelIndex === -1) {
    console.warn('movePanelToDiffSection: Panel and section not found', {
      activePanelRefId: active.panelRef.id,
      targetSectionRefId: target.sectionRef.id,
      activeIndex: activePanelIndex,
      targetIndex: targetPanelIndex,
    });
    return [state, Actions.noOp(ref)];
  }

  const isMovingToSameSection = active.sectionRef.id === target.sectionRef.id;

  // Panel is moving to a different section, and should be handled by a different action
  // This is invalid with this action, so we no-op
  if (isMovingToSameSection) {
    console.error(
      'movePanelToDiffSection: Panel is moving to the same section, and should be handled by a different action'
    );
    return [state, Actions.noOp(ref)];
  }

  // Remove panel from active section
  const newActiveSectionPanelRefs = arrayRemove(
    activeSection.panelRefs,
    activePanelIndex
  );

  // Insert panel at the correct position in target section
  const newTargetSectionPanelRefs = arrayInsert(
    targetSection.panelRefs,
    active.panelRef,
    target.insertDir,
    targetPanelIndex
  );

  // Determine the undo target for moving the panel back to its original position
  // For undo to work correctly, we need to find which panel should be the target
  // to restore the original order
  let undoTargetPanelRef = null;
  let undoInsertDir: 'before' | 'after' = 'before';

  if (newActiveSectionPanelRefs.length > 0) {
    // If we were originally at position 0, insert before the current first panel
    if (activePanelIndex === 0) {
      undoTargetPanelRef = newActiveSectionPanelRefs[0];
      undoInsertDir = 'before';
    } else if (activePanelIndex > newActiveSectionPanelRefs.length) {
      // If our original position is beyond the current array, insert after the last panel
      undoTargetPanelRef =
        newActiveSectionPanelRefs[newActiveSectionPanelRefs.length - 1];
      undoInsertDir = 'after';
    } else {
      // Otherwise, find the panel that's now at our original position and insert before it
      undoTargetPanelRef = newActiveSectionPanelRefs[activePanelIndex];
      undoInsertDir = 'before';
    }
  }

  // Update state with both sections' changes
  let newState = setInShallowClone(
    state,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'panelRefs'],
    newActiveSectionPanelRefs
  );
  newState = setInShallowClone(
    newState,
    ['parts', target.sectionRef.type, target.sectionRef.id, 'panelRefs'],
    newTargetSectionPanelRefs
  );

  // For undo, we need to move the panel from its new location back to its original location
  // If there's a panel at the original position, use it as the target
  // Otherwise, if the section is empty, we'll need to handle that case
  const undoAction = undoTargetPanelRef
    ? Actions.movePanelToDiffSection(
        ref,
        {
          panelRef: active.panelRef, // The panel we just moved
          sectionRef: target.sectionRef, // It's now in the target section
        },
        {
          insertDir: undoInsertDir,
          panelRef: undoTargetPanelRef, // The panel now at the original position
          sectionRef: active.sectionRef, // In the original section
        }
      )
    : Actions.movePanelToDiffSection(
        ref,
        {
          panelRef: active.panelRef,
          sectionRef: target.sectionRef,
        },
        {
          // If section is empty, we'll move to the first position (using the panel itself as target)
          insertDir: 'before' as const,
          panelRef: null,
          sectionRef: active.sectionRef,
        }
      );

  return [newState, undoAction];
};
